import { useEffect, useRef, useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, apiError, API_BASE } from "@/lib/api";
import { Loader2, ArrowRight, ShieldCheck, WifiOff, Building2, Delete, CheckCircle2, AlertTriangle } from "lucide-react";
import axios from "axios";
import OtpChallenge from "@/pages/OtpChallenge";

// Local portal backend (holds the 2FA challenge + verify endpoints). We hit
// it directly rather than through `lib/api.js` which is pinned to prod.
const PORTAL_BACKEND = process.env.REACT_APP_BACKEND_URL;
const portalApi = axios.create({ baseURL: PORTAL_BACKEND });

// Roles that must clear the 2FA challenge (matches backend ELEVATED_ROLES /
// frontend permissions.js SUPERVISOR_TIER).
const ELEVATED_ROLES = new Set([
  "supervisor", "superintendent", "owner", "site_admin", "site-admin", "control", "admin",
]);
function isElevated(role) {
  const r = String(role || "").toLowerCase();
  if (!r) return false;
  if (ELEVATED_ROLES.has(r)) return true;
  return r.split(/[^a-z0-9]+/).some((t) => ELEVATED_ROLES.has(t));
}

export default function Login() {
  const { user, refresh } = useAuth();
  const [username, setUsername] = useState("");
  const [pin, setPin] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [connected, setConnected] = useState(null);
  // 2FA state — when set, we render the OtpChallenge screen instead of the
  // sign-in form. `preAuthUser` holds the profile we've already authenticated
  // against prod so we can finalise the session after the OTP passes.
  const [twoFA, setTwoFA] = useState(null); // { username, email, emailHint }
  const [preAuthUser, setPreAuthUser] = useState(null);
  const [forgotOpen, setForgotOpen] = useState(false);
  const nav = useNavigate();
  const pinInputRef = useRef(null);

  useEffect(() => {
    axios.get(`${API_BASE}/api/health`, { timeout: 8000 })
      .then((r) => setConnected(!!r.data?.ok))
      .catch(() => setConnected(false));
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (busy) return;
      const active = document.activeElement;
      const isUsernameField = active && active.id === "username";
      if (/^[0-9]$/.test(e.key)) {
        if (isUsernameField) return;
        e.preventDefault();
        tapDigit(e.key);
      } else if (e.key === "Backspace" && !isUsernameField) {
        e.preventDefault();
        backspace();
      } else if (e.key === "Escape") {
        e.preventDefault();
        clearPin();
      } else if (e.key === "Enter") {
        if (pin.length === 4 && username.trim()) {
          e.preventDefault();
          submit();
        }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [pin, username, busy]);

  if (user) return <Navigate to="/" replace />;

  const finalise = async () => {
    // Session cookie / token already set by the prod /auth/login response
    // above. Hydrating via /auth/me sets the AuthContext user state which
    // triggers the <Navigate to="/" /> guard on next render.
    await refresh();
    nav("/");
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    if (busy) return;
    const currentPin = e?.__pin ?? pin;
    if (!username.trim() || currentPin.length !== 4) {
      setErr("Enter a username and 4-digit PIN.");
      return;
    }
    setErr(""); setBusy(true);
    try {
      // 1. Authenticate against prod. Token/cookie is set on success but the
      //    AuthContext user is NOT populated yet (we do that after 2FA).
      const { data } = await api.post("/auth/login", { username: username.trim(), pin: currentPin });
      const token = data?.token || data?.access_token || data?.jwt || data?.session_token;
      if (token) localStorage.setItem("sl_token", token);
      let profile = data?.user || null;
      if (!profile) {
        const meRes = await api.get("/auth/me");
        profile = meRes.data;
      }
      // Success — clear any lockout counter for this username.
      portalApi.post("/api/auth/pin-attempt-succeeded", { username: username.trim() }).catch(() => {});
      // 2. Non-elevated roles: no 2FA — hydrate and go.
      if (!isElevated(profile?.role)) {
        setPreAuthUser(profile);
        await finalise();
        return;
      }
      // 3. Elevated roles: ask the local portal backend whether 2FA is due
      //    (3-day grace period logic). If the user was verified in the last
      //    72h, `requires_2fa` will be false and we breeze straight through.
      let challenge;
      try {
        const res = await portalApi.post("/api/auth/2fa/challenge", {
          username: username.trim(),
          email: profile?.email || undefined,
        });
        challenge = res.data;
      } catch {
        // Local backend unreachable — do NOT lock the user out. Skip 2FA and
        // finalise so the app remains usable if the portal service is down.
        challenge = { requires_2fa: false, reason: "challenge_unavailable" };
      }
      if (!challenge?.requires_2fa) {
        setPreAuthUser(profile);
        await finalise();
        return;
      }
      // 4. Show the chunky 6-box OTP screen.
      setPreAuthUser(profile);
      setTwoFA({
        username: username.trim(),
        email: profile?.email || "",
        emailHint: challenge.email_hint || maskEmail(profile?.email),
      });
    } catch (ex) {
      const msg = apiError(ex);
      // Report the failed attempt to the local lockout tracker (fire and
      // forget). If it comes back with `is_locked: true` we surface a
      // stronger error banner so the user knows they must reset.
      const uname = username.trim();
      const status = ex?.response?.status;
      if (uname && (status === 401 || status === 400 || /pin|wrong|invalid/i.test(msg))) {
        try {
          const { data: lock } = await portalApi.post("/api/auth/pin-attempt-failed", {
            username: uname,
            reason: `login_${status || "err"}`,
          });
          if (lock?.is_locked) {
            setErr("Account locked after 5 wrong PINs. Reset via 'Forgot PIN?' below or ask your supervisor to unlock.");
          } else if (typeof lock?.failed_attempts === "number") {
            const left = Math.max(0, (lock.threshold || 5) - lock.failed_attempts);
            setErr(left <= 2
              ? `${msg} — ${left} attempt${left === 1 ? "" : "s"} left before your account locks.`
              : msg);
          } else {
            setErr(msg);
          }
        } catch {
          setErr(msg);
        }
      } else {
        setErr(msg);
      }
      setPin("");
      localStorage.removeItem("sl_token");
    } finally { setBusy(false); }
  };

  const onOtpVerified = async () => {
    // OTP verified locally — the 72h grace stamp is now saved. Hydrate the
    // AuthContext and route into the app.
    setTwoFA(null);
    await finalise();
  };

  const onOtpCancel = () => {
    // User bailed on OTP — drop the prod session so they have to sign in
    // again from a clean state.
    localStorage.removeItem("sl_token");
    setTwoFA(null);
    setPreAuthUser(null);
    setPin("");
  };

  const tapDigit = (d) => {
    if (pin.length >= 4) return;
    const next = pin + d;
    setPin(next);
    if (next.length === 4) {
      // Pass the fresh pin explicitly to avoid stale closure
      setTimeout(() => submit({ preventDefault() {}, __pin: next }), 120);
    }
  };
  const backspace = () => setPin((p) => p.slice(0, -1));
  const clearPin = () => setPin("");

  // If a 2FA challenge is pending, take over the whole screen with the
  // chunky 6-box OTP UI (same component used by the registration flow).
  if (twoFA) {
    return (
      <OtpChallenge
        username={twoFA.username}
        emailHint={twoFA.emailHint}
        verifyPath="/auth/verify-otp"
        resendPath="/auth/resend-otp"
        title="Verify your sign-in"
        subtitle="Enter the 6-digit code we just sent to"
        onVerified={onOtpVerified}
        onCancel={onOtpCancel}
      />
    );
  }

  return (
    <div className="min-h-screen relative overflow-hidden flex flex-col" data-testid="login-page">
      {/* Digger background — full-bleed, dark for readability */}
      <div
        className="absolute inset-0"
        aria-hidden="true"
        style={{
          backgroundImage: "url('/digger-bg.png')",
          backgroundSize: "cover",
          backgroundPosition: "center 60%",
          backgroundRepeat: "no-repeat",
        }}
        data-testid="login-digger-bg"
      />
      {/* Dark overlay so the form + logo stay readable on top of the photo */}
      <div
        className="absolute inset-0"
        aria-hidden="true"
        style={{
          background:
            "linear-gradient(180deg, rgba(10,10,10,0.72) 0%, rgba(10,10,10,0.55) 45%, rgba(10,10,10,0.85) 100%)",
        }}
      />
      {/* Warm signature glow layers */}
      <div
        className="absolute inset-0"
        aria-hidden="true"
        style={{
          background:
            "radial-gradient(ellipse 80% 60% at 20% 100%, rgba(245,130,32,0.18), transparent 60%)," +
            "radial-gradient(ellipse 60% 50% at 90% 0%, rgba(31,48,85,0.18), transparent 60%)",
        }}
      />
      {/* Subtle grid texture */}
      <div
        className="absolute inset-0 opacity-[0.06]"
        aria-hidden="true"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)",
          backgroundSize: "48px 48px",
          maskImage: "radial-gradient(ellipse at center, black 30%, transparent 80%)",
          WebkitMaskImage: "radial-gradient(ellipse at center, black 30%, transparent 80%)",
        }}
      />
      {/* Warm bottom vignette */}
      <div
        className="absolute inset-x-0 bottom-0 h-1/3"
        aria-hidden="true"
        style={{
          background: "linear-gradient(to top, rgba(245,130,32,0.10), transparent)",
        }}
      />
      {/* Orange top accent */}
      <div className="relative h-1 w-full" style={{ background: "var(--primary)" }} />

      {/* Content */}
      <div className="relative flex-1 flex flex-col items-center justify-center px-4 py-10">
        <div className="w-full max-w-2xl">
          {/* Floating logo — no wrapper, no bg, no border, no box */}
          <div className="flex flex-col items-center text-center mb-6" data-testid="login-brand">
            <img
              src="/shiftlog-logo.png?v=pl2"
              alt="PIT LINK"
              className="w-[720px] max-w-full h-auto select-none pointer-events-none sl-logo-glow-strong"
              style={{
                background: "transparent",
                border: "none",
                boxShadow: "none",
              }}
              draggable="false"
              data-testid="brand-logo"
            />

            {/* Sharper, tier-one heading */}
            <h1
              className="mt-6 font-display uppercase text-white"
              style={{
                fontWeight: 900,
                fontSize: "clamp(2.25rem, 4.5vw, 3rem)",
                letterSpacing: "0.02em",
                lineHeight: 0.95,
                textShadow: "0 4px 20px rgba(0,0,0,0.7)",
              }}
              data-testid="login-heading"
            >
              MAINTENANCE HUB<br />
              <span style={{ color: "var(--primary)" }}>PORTAL</span>
            </h1>
            <div className="orange-accent-bar mt-4" />
            <div className="mt-3 text-[11px] uppercase tracking-[0.3em] font-black text-white/70">
              Field Edition · v1.0
            </div>
          </div>

          {/* Frosted-glass sign-in card — kept narrow so the giant SE logo above stays the visual centrepiece */}
          <div
            className="relative overflow-hidden mx-auto max-w-md"
            style={{
              background: "rgba(20, 20, 20, 0.72)",
              backdropFilter: "blur(18px) saturate(1.2)",
              WebkitBackdropFilter: "blur(18px) saturate(1.2)",
              border: "1px solid rgba(255,255,255,0.08)",
              borderRadius: 20,
              boxShadow: "0 30px 80px -30px rgba(0,0,0,0.9), inset 0 1px 0 rgba(255,255,255,0.06)",
              padding: "1.5rem",
            }}
            data-testid="login-panel"
          >
            <div className="flex items-center justify-between mb-1">
              <h2 className="font-display uppercase tracking-tight text-white" style={{ fontWeight: 900, fontSize: "1.5rem" }}>
                Sign in
              </h2>
              <span className="inline-block w-3 h-3" style={{ background: "var(--primary)" }} />
            </div>
            <p className="text-white/60 text-sm mb-6">Site personnel access</p>

            <form onSubmit={submit} className="space-y-5" data-testid="login-form">
              <div className="field">
                <label htmlFor="username">Username</label>
                <input
                  id="username"
                  data-testid="login-username"
                  className="input"
                  type="text"
                  autoComplete="username"
                  required
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="e.g. j smith"
                  style={{ background: "rgba(0,0,0,0.35)", border: "1px solid rgba(255,255,255,0.12)" }}
                />
              </div>

              <div className="field">
                <label>4-Digit PIN <span className="text-white/50 normal-case tracking-normal">· type or tap</span></label>
                <div className="flex justify-center gap-3 py-2" data-testid="pin-dots">
                  {[0, 1, 2, 3].map((i) => (
                    <div
                      key={i}
                      className="w-14 h-14 rounded-xl flex items-center justify-center transition"
                      style={{
                        border: pin.length > i ? "1px solid var(--primary)" : "1px solid rgba(255,255,255,0.15)",
                        background: pin.length > i ? "rgba(245,130,32,0.15)" : "rgba(0,0,0,0.35)",
                      }}
                    >
                      {pin.length > i && (
                        <div className="w-3.5 h-3.5 rounded-full" style={{ background: "var(--primary)" }} />
                      )}
                    </div>
                  ))}
                </div>

                <div className="grid grid-cols-3 gap-2 mt-2" data-testid="pin-pad">
                  {["1","2","3","4","5","6","7","8","9"].map((d) => (
                    <button
                      key={d}
                      type="button"
                      onClick={() => tapDigit(d)}
                      className="btn font-display text-2xl !min-h-[64px]"
                      style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.12)" }}
                      data-testid={`pin-digit-${d}`}
                    >
                      {d}
                    </button>
                  ))}
                  <button type="button" onClick={clearPin} className="btn btn-ghost !min-h-[64px] text-xs"
                          style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.12)" }}
                          data-testid="pin-clear">
                    Clear
                  </button>
                  <button type="button" onClick={() => tapDigit("0")}
                          className="btn font-display text-2xl !min-h-[64px]"
                          style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.12)" }}
                          data-testid="pin-digit-0">
                    0
                  </button>
                  <button type="button" onClick={backspace} className="btn btn-ghost !min-h-[64px]"
                          style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.12)" }}
                          data-testid="pin-backspace">
                    <Delete className="w-5 h-5" />
                  </button>
                </div>

                <input
                  ref={pinInputRef}
                  type="password"
                  inputMode="numeric"
                  maxLength={4}
                  pattern="\d{4}"
                  value={pin}
                  onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 4))}
                  className="sr-only"
                  data-testid="login-pin"
                />
              </div>

              {err && (
                <div className="pill pill-red !text-sm !py-2.5 !px-3.5 justify-start" data-testid="login-error">
                  {err}
                </div>
              )}

              <button
                type="submit"
                className="btn btn-primary w-full !h-14 text-base"
                disabled={busy || pin.length !== 4 || !username.trim()}
                data-testid="login-submit"
              >
                {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : (<>Sign In <ArrowRight className="w-5 h-5" /></>)}
              </button>

              <div className="flex items-center justify-center pt-1">
                <button
                  type="button"
                  onClick={() => setForgotOpen(true)}
                  className="text-[11px] uppercase tracking-[0.25em] font-black transition-colors"
                  style={{ color: "rgba(245,130,32,0.85)" }}
                  data-testid="login-forgot-pin"
                >
                  Forgot PIN?
                </button>
              </div>
            </form>

            <div
              className="mt-5 pt-4 flex items-center justify-between text-[11px] uppercase tracking-widest"
              style={{ borderTop: "1px solid rgba(255,255,255,0.08)" }}
              data-testid="connectivity"
            >
              <span className="text-white/60">Backend</span>
              {connected === null && (
                <span className="flex items-center gap-1.5 text-white/60">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" /> Checking…
                </span>
              )}
              {connected === true && (
                <span className="flex items-center gap-1.5 pill pill-green !text-[10px]" data-testid="conn-ok">
                  <CheckCircle2 className="w-3.5 h-3.5" /> Live
                </span>
              )}
              {connected === false && (
                <span className="flex items-center gap-1.5 pill pill-red !text-[10px]" data-testid="conn-fail">
                  <AlertTriangle className="w-3.5 h-3.5" /> Unreachable
                </span>
              )}
            </div>
          </div>

          <div className="mt-6 flex items-center justify-center gap-5 text-[10px] uppercase tracking-[0.25em] text-white/60">
            <span className="flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5" /> Secure</span>
            <span>·</span>
            <span className="flex items-center gap-1.5"><Building2 className="w-3.5 h-3.5" /> Site-Isolated</span>
            <span>·</span>
            <span className="flex items-center gap-1.5"><WifiOff className="w-3.5 h-3.5" /> Offline-Ready</span>
          </div>
          <div className="text-center text-[10px] text-white/40 mt-2">© Pit Link Systems</div>
        </div>
      </div>
      {forgotOpen && (
        <ForgotPinModal
          initialUsername={username.trim()}
          onClose={() => setForgotOpen(false)}
          onDone={() => { setForgotOpen(false); setErr("PIN reset — sign in with your new PIN."); }}
        />
      )}
    </div>
  );
}

function maskEmail(email) {
  if (!email || !email.includes("@")) return "your email";
  const [l, d] = email.split("@");
  return `${l.slice(0, 1)}***@${d}`;
}

/**
 * ForgotPinModal — 3-step self-serve PIN reset (username → OTP → new PIN).
 * Talks exclusively to the local portal backend (`/api/auth/forgot-pin/*`)
 * which is the reference implementation for the shared prod team.
 */
function ForgotPinModal({ initialUsername, onClose, onDone }) {
  const [step, setStep] = useState(1); // 1: enter username, 2: enter OTP, 3: enter new PIN
  const [username, setUsername] = useState(initialUsername || "");
  const [emailHint, setEmailHint] = useState("");
  const [otp, setOtp] = useState("");
  const [resetToken, setResetToken] = useState("");
  const [pin, setPin] = useState("");
  const [pin2, setPin2] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const start = async () => {
    const u = username.trim();
    if (!u) { setErr("Enter your username."); return; }
    setBusy(true); setErr("");
    try {
      const { data } = await portalApi.post("/api/auth/forgot-pin/start", { username: u });
      setEmailHint(data?.email_hint || "");
      setStep(2);
    } catch (ex) {
      setErr(apiError(ex));
    } finally { setBusy(false); }
  };

  const verify = async () => {
    if (!/^\d{6}$/.test(otp)) { setErr("Enter the 6-digit code."); return; }
    setBusy(true); setErr("");
    try {
      const { data } = await portalApi.post("/api/auth/forgot-pin/verify", { username: username.trim(), otp });
      if (!data?.pin_reset_token) throw new Error("Verification failed.");
      setResetToken(data.pin_reset_token);
      setStep(3);
    } catch (ex) {
      setErr(apiError(ex));
    } finally { setBusy(false); }
  };

  const reset = async () => {
    if (!/^\d{4}$/.test(pin)) { setErr("PIN must be 4 digits."); return; }
    if (pin !== pin2) { setErr("PINs don't match."); return; }
    setBusy(true); setErr("");
    try {
      await portalApi.post("/api/auth/forgot-pin/reset", {
        username: username.trim(),
        pin_reset_token: resetToken,
        new_pin: pin,
      });
      onDone();
    } catch (ex) {
      setErr(apiError(ex));
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="forgot-pin-modal">
      <div className="absolute inset-0" style={{ background: "rgba(0,0,0,0.78)", backdropFilter: "blur(6px)" }} onClick={onClose} />
      <div className="relative w-full max-w-md p-6" style={{ background: "rgba(20,20,26,0.95)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 16 }}>
        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center"
               style={{ background: "rgba(245,130,32,0.15)", color: "var(--primary)", border: "1px solid rgba(245,130,32,0.3)" }}>
            <ShieldCheck className="w-5 h-5" />
          </div>
          <div className="flex-1">
            <div className="text-[10px] uppercase tracking-widest text-white/60">Forgot PIN</div>
            <div className="font-display font-black text-lg tracking-tight">
              {step === 1 && "Confirm your username"}
              {step === 2 && "Enter reset code"}
              {step === 3 && "Choose new PIN"}
            </div>
          </div>
          <button type="button" onClick={onClose} className="btn btn-ghost btn-sm !px-2" data-testid="forgot-close">
            ✕
          </button>
        </div>

        {err && (
          <div className="pill pill-red !text-xs !py-2 !px-3 justify-start mb-3" data-testid="forgot-error">
            <AlertTriangle className="w-3.5 h-3.5" /> {err}
          </div>
        )}

        {step === 1 && (
          <div className="space-y-3">
            <div className="text-[11px] text-white/60">Enter your username and we&apos;ll email a 6-digit code to the address on file.</div>
            <input
              className="input !py-3 !text-base"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="your username"
              data-testid="forgot-username"
              onKeyDown={(e) => e.key === "Enter" && start()}
              autoFocus
            />
            <button className="btn btn-primary w-full !h-12" onClick={start} disabled={busy} data-testid="forgot-send">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />} Send reset code
            </button>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-3">
            <div className="text-[11px] text-white/60">
              Code sent to <span className="text-white font-mono-tight">{emailHint || "your registered email"}</span>. Check spam if you don&apos;t see it.
            </div>
            <input
              className="input !py-3 !text-2xl tracking-widest text-center font-mono-tight"
              inputMode="numeric"
              maxLength={6}
              value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
              placeholder="••••••"
              data-testid="forgot-otp"
              onKeyDown={(e) => e.key === "Enter" && verify()}
              autoFocus
            />
            <button className="btn btn-primary w-full !h-12" onClick={verify} disabled={busy || otp.length !== 6} data-testid="forgot-verify">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />} Verify
            </button>
            <button className="text-[11px] text-white/60 hover:text-white transition-colors w-full text-center" onClick={() => setStep(1)}>
              ← Wrong username?
            </button>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-3">
            <div className="text-[11px] text-white/60">Pick a fresh 4-digit PIN. Don&apos;t reuse your old one.</div>
            <input
              className="input !py-3 !text-2xl tracking-widest text-center font-mono-tight"
              inputMode="numeric" type="password" maxLength={4}
              value={pin}
              onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 4))}
              placeholder="••••"
              data-testid="forgot-newpin"
              autoFocus
            />
            <input
              className="input !py-3 !text-2xl tracking-widest text-center font-mono-tight"
              inputMode="numeric" type="password" maxLength={4}
              value={pin2}
              onChange={(e) => setPin2(e.target.value.replace(/\D/g, "").slice(0, 4))}
              placeholder="confirm"
              data-testid="forgot-newpin2"
              onKeyDown={(e) => e.key === "Enter" && reset()}
            />
            <button className="btn btn-primary w-full !h-12" onClick={reset} disabled={busy || pin.length !== 4} data-testid="forgot-reset">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Save new PIN
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
